'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FlightIcon from '@mui/icons-material/Flight';
import AssignmentIcon from '@mui/icons-material/Assignment';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { useThemeMode } from '@/theme/AppThemeProvider';

export default function HomePage() {
  const router = useRouter();
  const supabase = createClient();
  const [loading, setLoading] = useState(true);
  const { mode, toggleTheme } = useThemeMode();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        router.push('/login');
      } else {
        setLoading(false);
      }
    };
    checkAuth();
  }, [router, supabase.auth]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: 'var(--primary)' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3 },
        position: 'relative',
      }}
    >
      {/* Theme Toggle */}
      <Tooltip title={mode === 'dark' ? 'Light Mode' : 'Dark Mode'}>
        <IconButton
          onClick={toggleTheme}
          sx={{ position: 'absolute', top: 16, right: 16 }}
        >
          {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>
      </Tooltip>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 1,
          color: 'var(--on-surface)',
          fontSize: { xs: '1.5rem', sm: '2rem' },
        }}
      >
        Willkommen zurück
      </Typography>
      <Typography
        variant="body1"
        sx={{ mb: 5, color: 'var(--on-surface-variant)' }}
      >
        Wähle einen Bereich
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: { xs: 2, sm: 3 },
          maxWidth: 560,
          width: '100%',
        }}
      >
        {/* Reisen Section */}
        <Card
          sx={{
            bgcolor: 'var(--surface-container-lowest)',
            border: '1px solid var(--border)',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: 'var(--shadow-md)' },
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
        >
          <CardActionArea onClick={() => router.push('/travel')} sx={{ p: { xs: 2.5, sm: 3 } }}>
            <CardContent sx={{ textAlign: 'center', p: '0 !important' }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '16px',
                  background: 'var(--primary-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <FlightIcon sx={{ fontSize: 32, color: 'var(--primary)' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--on-surface)' }}>
                Reisen
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'var(--on-surface-variant)', lineHeight: 1.5 }}>
                Reise-Planung, Orte, geteilte Ausgaben & Chat
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>

        {/* Projekt-Management Section */}
        <Card
          sx={{
            bgcolor: 'var(--surface-container-lowest)',
            border: '1px solid var(--border)',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: 'var(--shadow-md)' },
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
        >
          <CardActionArea onClick={() => router.push('/projekte')} sx={{ p: { xs: 2.5, sm: 3 } }}>
            <CardContent sx={{ textAlign: 'center', p: '0 !important' }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '16px',
                  background: 'rgba(139, 111, 214, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <AssignmentIcon sx={{ fontSize: 32, color: 'var(--accent-lilac)' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--on-surface)' }}>
                Projekte
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'var(--on-surface-variant)', lineHeight: 1.5 }}>
                Tracking: Ausgaben, Kilometer, Zeit & Tasks
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      </Box>
    </Box>
  );
}
